import React, { useState } from 'react'
import produce from 'immer'
import Button from 'src/components/base/Button'
import ExpandedTable from 'src/components/base/ExpandedTable'
import { confirmDeletePopup } from 'src/components/popup/confirmDeletePopup'
import { Config, LatLon, Quay, TerminalPlannerBerth } from 'src/types'

import BerthModal from './modals/BerthModal'
import { INITIAL_BERTH } from './constants'

export type TPropsQuayConfigBerthSection = {
  config: Quay
  setConfig: React.Dispatch<React.SetStateAction<Config>>
  customFetch: (url: string, options: RequestInit, customResolver?: (r: Response) => any) => Promise<any>
}

function QuayConfigBerthSection({ config, setConfig, customFetch }: TPropsQuayConfigBerthSection) {
  const [selectedBerth, setSelectedBerth] = useState<TerminalPlannerBerth | null>(null)

  const onAddBerth = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.stopPropagation()
    setSelectedBerth(INITIAL_BERTH)
  }

  const onRemoveBerth = (index: number, berth: TerminalPlannerBerth) => {
    confirmDeletePopup({
      itemName: berth.terminalName,
      onConfirm: () => {
        setConfig(
          produce((draft: Config) => {
            draft.quays.find(quay => quay.name === config.name)?.berths.splice(index, 1)
          })
        )
      },
      alert: true
    })
  }

  return (
    <>
      <ExpandedTable emptyMessage="No berths defined" empty={config?.berths.length === 0}>
        <ExpandedTable.Header>
          <ExpandedTable.Title>Berths</ExpandedTable.Title>
          <ExpandedTable.Actions>
            <Button variant="brand-tertiary" icon="fa fa-plus" onClick={onAddBerth}>
              Add berth
            </Button>
          </ExpandedTable.Actions>
        </ExpandedTable.Header>
        <ExpandedTable.Body>
          <ExpandedTable.Row>
            <ExpandedTable.Cell asHeader style={{ width: '30%' }}>
              Berth name
            </ExpandedTable.Cell>
            <ExpandedTable.Cell asHeader style={{ width: '20%' }}>
              Bollard start
            </ExpandedTable.Cell>
            <ExpandedTable.Cell asHeader style={{ width: '20%' }}>
              Bollard end
            </ExpandedTable.Cell>
            <ExpandedTable.Cell asHeader style={{ width: '20%' }}>
              Berth name (map)
            </ExpandedTable.Cell>
            <ExpandedTable.Cell asHeader style={{ width: '10%' }} />
          </ExpandedTable.Row>
          {config?.berths.map((berth, index) => {
            return (
              <ExpandedTable.Row key={index}>
                <ExpandedTable.Cell style={{ width: '30%' }}>{berth.terminalName}</ExpandedTable.Cell>
                <ExpandedTable.Cell style={{ width: '20%' }}>{berth.startBollard}</ExpandedTable.Cell>
                <ExpandedTable.Cell style={{ width: '20%' }}>{berth.endBollard}</ExpandedTable.Cell>
                <ExpandedTable.Cell style={{ width: '20%' }}>{berth.berthId}</ExpandedTable.Cell>
                <ExpandedTable.Cell className="align-right" style={{ width: '10%' }}>
                  <ExpandedTable.CellActions>
                    <Button
                      onClick={() => setSelectedBerth(berth)}
                      size="small"
                      variant="neutral-tertiary"
                      icon="fa fa-edit"
                    />
                    <Button
                      onClick={() => {
                        onRemoveBerth(index, berth)
                      }}
                      size="small"
                      variant="neutral-tertiary"
                      icon="fa fa-trash"
                    />
                  </ExpandedTable.CellActions>
                </ExpandedTable.Cell>
              </ExpandedTable.Row>
            )
          })}
        </ExpandedTable.Body>
      </ExpandedTable>

      {selectedBerth && (
        <BerthModal
          customFetch={customFetch}
          selectedBerth={selectedBerth}
          selectedQuay={config}
          isOpen={!!selectedBerth}
          onBerthConfirm={berth => {
            if (berth) {
              setConfig(
                produce((draft: Config) => {
                  const quay = draft.quays.find(quay => quay.name === config.name)
                  const berthIndex = quay.berths.findIndex(b => b.berthId === berth.berthId)
                  if (berthIndex === -1) {
                    quay.berths.push(berth)
                  } else {
                    quay.berths[berthIndex] = berth
                  }
                })
              )
            }
            setSelectedBerth(null)
          }}
          onCancel={() => setSelectedBerth(null)}
        />
      )}
    </>
  )
}

export default QuayConfigBerthSection
